import { logger, SizeModeEnum, toBig } from '@talos/kyoko';
import { parse } from 'date-fns';
import { entries } from 'lodash-es';
import { v4 as uuid } from 'uuid';
import { UNITS_CURRENCY } from '../../../../../components/OMS/CareOrderFormView/types';
import { convertDateToDefaultCareOrderUTCDate } from '../../../../../components/OMS/CareOrderFormView/utils';
import { type OrgConfigurationContextProps, OrgConfigurationKey } from '../../../../../providers';
import { EMPTY_IMPORT_CARE_ORDER_ACTION, type ImportedCareOrder } from '../types';
import { getImportedCareOrdersWithActions } from '../utils';
import type { CareOrderImportContext } from './types';

export interface UnqorkOrder {
  id: string;
  orderNumber: string;
  tradeDate: string;
  settlementDate: string;
  totalShares: number;
  orderType: string;
  settlementType: string;
  authorizedParticipant: string;
  liquidityProvider: string | null; // <-- have seen this be null, valid
  baskets: number;
  totalAssets: number;
  status: string;
  productId: string;
  assetId: string;
}

interface UnqorkProduct {
  /** The Unqork ProductID */
  ProductID: string;
  /** The Talos Basket.BasketSymbol */
  BasketSymbol: string;
}

interface UnqorkAuthorizedParticipant {
  /** The Unqork AP value */
  AuthorizedParticipant: string;
  /** The talos Counterparty.Name */
  Counterparty: string;
}

export function unqorkOrdersToImportedCareOrders(
  unqorkOrders: UnqorkOrder[],
  context: CareOrderImportContext
): ImportedCareOrder[] {
  const { baskets, allCareOrders } = context;

  const apMap = getAuthorizedParticipantMapping(context.getConfig);
  const productMap = getProductIDMapping(context.getConfig);
  const useFundDefs = context.getConfig(OrgConfigurationKey.UseFundDefinitionAndTradableIndexesStreams, false);

  const importedCareOrders: ImportedCareOrder[] = [];
  for (const unqorkOrder of unqorkOrders) {
    const basketSymbol = productMap?.get(unqorkOrder.productId)?.BasketSymbol;
    const basket = baskets?.find(b => b.BasketSymbol === basketSymbol);
    if (!basket) {
      throw new Error(`Unable to resolve a Basket from this order. Product ID ${unqorkOrder.productId}.`);
    }

    const counterpartyName = apMap?.get(unqorkOrder.authorizedParticipant)?.Counterparty;
    if (unqorkOrder.authorizedParticipant && !counterpartyName) {
      throw new Error(
        `Found an AP property on the unqork order, but was not able to resolve any Talos counterparty. AP: ${unqorkOrder.authorizedParticipant}.`
      );
    }

    const unitQty = toBig(unqorkOrder.totalShares)?.div(10_000);
    if (!unitQty) {
      throw new Error(`Was not able to grab any totalShares. Raw totalShares: ${unqorkOrder.totalShares}.`);
    }

    const settleDate = unqorkOrderSettleDateToISOSettleDate(unqorkOrder.settlementDate);
    if (!settleDate) {
      throw new Error(`Was not able to parse any settle date. Raw settle date: ${unqorkOrder.settlementDate}.`);
    }

    importedCareOrders.push({
      ClOrdID: uuid(),
      Side: unqorkOrder.orderType === 'Creation' ? 'Buy' : 'Sell',
      Group: unqorkOrder.orderNumber ?? undefined,
      Counterparty: counterpartyName,
      SettleDate: settleDate,
      Annotations: {
        ETP: {
          InitiatingFrom: unqorkOrder.liquidityProvider ?? undefined, // normalize null to undefined
        },
        BasketOrder: {
          BasketSymbol: basket.BasketSymbol,
          BasketOrderQty: useFundDefs ? undefined : unitQty.toFixed(),
        },
      },

      OrderQty: unitQty.toFixed(),
      units: unitQty.toFixed(),
      Currency: UNITS_CURRENCY,
      Symbol: undefined, // Not relevant for orders placed in units
      SizeMode: useFundDefs ? SizeModeEnum.Units : undefined,

      action: EMPTY_IMPORT_CARE_ORDER_ACTION,
    });
  }

  return getImportedCareOrdersWithActions(importedCareOrders, allCareOrders, 'unqork');
}

export function unqorkOrderSettleDateToISOSettleDate(dateString: string): string | undefined {
  try {
    const localDate = parse(dateString, 'yyyy-MM-dd', new Date());
    const standardizedDate = convertDateToDefaultCareOrderUTCDate(localDate);
    const isoString = standardizedDate.toISOString();
    return isoString;
  } catch (e) {
    return undefined;
  }
}

function getProductIDMapping(
  getConfig: OrgConfigurationContextProps['getConfig']
): Map<string, UnqorkProduct> | undefined {
  const stringConfig = getConfig(OrgConfigurationKey.UnqorkProductIDMap, '');
  if (stringConfig === '') {
    return undefined;
  }

  try {
    const config: Record<string, UnqorkProduct> = JSON.parse(stringConfig);
    return new Map(entries(config));
  } catch (e) {
    logger.error(new Error('Failed to parse rfq.UnqorkProductIDMap'), { extra: { stringConfig } });
    return undefined;
  }
}

function getAuthorizedParticipantMapping(
  getConfig: OrgConfigurationContextProps['getConfig']
): Map<string, UnqorkAuthorizedParticipant> | undefined {
  const stringConfig = getConfig(OrgConfigurationKey.UnqorkAuthorizedParticipantMap, '');
  if (stringConfig === '') {
    return undefined;
  }

  try {
    const config: Record<string, UnqorkAuthorizedParticipant> = JSON.parse(stringConfig);
    return new Map(entries(config));
  } catch (e) {
    logger.error(new Error('Failed to parse rfq.UnqorkAuthorizedParticipantMap'), { extra: { stringConfig } });
    return undefined;
  }
}
